import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dataService } from '@/services/dataService';
import { Partnership, Contract } from '@/types/entities';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { SafeLink } from '@/components/ui/SafeLink';
import { useI18n } from '@/i18n';
import { useToast } from '@/components/ui/Toast';
import { pushRecentlyViewed } from '@/lib/recentlyViewed';
import { ProfitSharingCalculator } from './ProfitSharingCalculator';

type Tab = 'overview' | 'contacts' | 'contract' | 'profitSharing' | 'ledger' | 'documents' | 'activity';
const TABS: Tab[] = ['overview', 'contacts', 'contract', 'profitSharing', 'ledger', 'documents', 'activity'];

export function PartnershipDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const push = useToast();
  const [partnership, setPartnership] = useState<Partnership | null | undefined>(undefined);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [tab, setTab] = useState<Tab>('overview');
  const [contactForm, setContactForm] = useState({ name: '', role: 'primary', phone: '', email: '' });
  const [noteText, setNoteText] = useState('');
  const [fileName, setFileName] = useState('');

  function reload() {
    if (!id) return;
    dataService.getPartnership(id).then((p) => {
      setPartnership(p ?? null);
      if (p) pushRecentlyViewed({ id: p.id, label: `${p.id} — ${p.partnerName}`, route: `/partnerships/${p.id}` });
    });
    dataService.listContracts().then((all) => setContracts(all.filter((c) => c.partnershipId === id)));
  }
  useEffect(reload, [id]);

  if (partnership === undefined) return <div className="card" style={{ padding: 20 }}>Loading…</div>;
  if (partnership === null) {
    return (
      <div className="card" style={{ padding: 20 }}>
        <div style={{ fontWeight: 700 }}>Partnership not found</div>
        <button onClick={() => navigate('/partnerships')} style={{ marginTop: 10 }}>Back to Partnerships</button>
      </div>
    );
  }

  async function addContact() {
    if (!partnership || !contactForm.name.trim()) return;
    await dataService.addPartnershipContact(partnership.id, contactForm);
    setContactForm({ name: '', role: 'primary', phone: '', email: '' });
    push(t('toast.contactAdded'), 'success');
    reload();
  }

  async function addNote() {
    if (!partnership || !noteText.trim()) return;
    await dataService.addPartnershipTimelineEntry(partnership.id, t('detail.manualActivity'), noteText.trim());
    setNoteText('');
    push(t('toast.activityAdded'), 'success');
    reload();
  }

  return (
    <div>
      <h1 style={{ marginBottom: 4 }}>{partnership.partnerName}</h1>
      <div style={{ color: 'var(--text-3)', marginBottom: 16 }}>{partnership.id} · <StatusBadge entity="partnership" statusKey={partnership.status} /></div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map((tKey) => (
          <button key={tKey} onClick={() => setTab(tKey)} className={tab === tKey ? 'tab-btn active' : 'tab-btn'}>
            {t(`partnershipDetail.tabs.${tKey}`)}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="card" style={{ padding: 16 }}>
          <p><b>Legal name:</b> {partnership.legalName ?? '—'}</p>
          <p><b>Type:</b> {partnership.type}</p>
          <p><b>Country:</b> {partnership.country} {partnership.emirate && `· ${partnership.emirate}`}</p>
          <p><b>Owner:</b> {partnership.ownerId}</p>
          <p><b>Website:</b> <SafeLink href={partnership.website}>{partnership.website ?? '—'}</SafeLink></p>
          <p><b>Start / End:</b> {partnership.startDate ?? '—'} → {partnership.endDate ?? '—'}</p>
          {partnership.notes && <p><b>Notes:</b> {partnership.notes}</p>}
        </div>
      )}

      {tab === 'contacts' && (
        <div className="card" style={{ padding: 16 }}>
          {partnership.contacts.length === 0 && <div style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 12 }}>{t('empty.noContacts')}</div>}
          {partnership.contacts.map((c) => (
            <div key={c.id} style={{ borderBottom: '1px solid var(--border)', padding: '8px 0', fontSize: 13 }}>
              <b>{c.name}</b> · {c.role} {c.jobTitle && `· ${c.jobTitle}`}
              <div style={{ color: 'var(--text-2)' }}>
                {c.phone && <SafeLink href={`tel:${c.phone}`}>{c.phone}</SafeLink>} {c.email && <>· <SafeLink href={`mailto:${c.email}`}>{c.email}</SafeLink></>}
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <input className="text-input" style={{ width: 160 }} placeholder="Name" value={contactForm.name} onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))} />
            <input className="text-input" style={{ width: 140 }} placeholder="Phone" value={contactForm.phone} onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))} />
            <input className="text-input" style={{ width: 180 }} placeholder="Email" value={contactForm.email} onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))} />
            <button className="btn-primary" onClick={addContact}>{t('partnershipDetail.addContact')}</button>
          </div>
        </div>
      )}

      {tab === 'contract' && (
        <div className="card" style={{ padding: 16 }}>
          {contracts.length === 0 ? (
            <>
              <div style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 10 }}>{t('empty.noContracts')}</div>
              <button className="btn-primary" onClick={() => navigate(`/contracts?new=${partnership.id}`)}>+ {t('contractForm.newContract')}</button>
            </>
          ) : (
            contracts.map((c) => (
              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => navigate('/contracts')}>
                <span>{c.id} — {c.title}</span>
                <StatusBadge entity="contract" statusKey={c.status} />
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'profitSharing' && <ProfitSharingCalculator partnershipId={partnership.id} onSaved={reload} />}

      {tab === 'ledger' && (
        <div className="card" style={{ padding: 16 }}>
          {partnership.ledger.length === 0 ? (
            <div style={{ color: 'var(--text-3)', fontSize: 13 }}>{t('partnershipDetail.ledgerEmpty')}</div>
          ) : (
            <div className="table-scroll">
            <table className="dt">
              <thead><tr><th>Period</th><th>Revenue</th><th>Partner Share</th><th>Ugarits Share</th><th>Balance</th></tr></thead>
              <tbody>
                {partnership.ledger.map((l) => (
                  <tr key={l.id}><td>{l.periodLabel}</td><td>{l.grossRevenue.toLocaleString()}</td><td>{l.partnerShare.toLocaleString()}</td><td>{l.ugaritsShare.toLocaleString()}</td><td>{l.balance.toLocaleString()}</td></tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {tab === 'documents' && (
        <div className="card" style={{ padding: 16 }}>
          <div className="soon-tag-inline" style={{ marginBottom: 10, display: 'inline-block' }}>{t('documents.previewOnly')}</div>
          <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 0 }}>{t('partnershipDetail.documentsEmpty')}</p>
          <input
            type="file"
            onChange={(e) => setFileName(e.target.files?.[0]?.name ?? '')}
          />
          {fileName && (
            <div style={{ marginTop: 10, fontSize: 13 }}>
              {t('documents.selected')}: {fileName}
              <button style={{ marginInlineStart: 8, color: 'var(--crit)' }} onClick={() => setFileName('')}>{t('documents.remove')}</button>
            </div>
          )}
        </div>
      )}

      {tab === 'activity' && (
        <div className="card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input className="text-input" placeholder={t('detail.addNotePlaceholder')} value={noteText} onChange={(e) => setNoteText(e.target.value)} />
            <button className="btn-primary" onClick={addNote}>{t('common.save')}</button>
          </div>
          {partnership.timeline.length === 0 ? (
            <div style={{ color: 'var(--text-3)', fontSize: 13 }}>No activity logged for this partnership yet.</div>
          ) : (
            partnership.timeline.map((e) => (
              <div key={e.id} style={{ borderBottom: '1px solid var(--border)', padding: '8px 0', fontSize: 13 }}>
                <div style={{ fontWeight: 600 }}>{e.label}</div>
                {e.note && <div style={{ color: 'var(--text-2)' }}>{e.note}</div>}
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{e.actor} · {new Date(e.createdAt).toLocaleString()}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
